import { Alert, AlertDescription, AlertIcon, Avatar, Box, Text, VStack } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { FormModal } from '../../../components/FormModal'
import { LoadingBlock } from '../../../components/Loading'
import { formatDateTime, useAppToast } from '../../../helpers'
import { HrNoteService } from '../../../services/HrNote/HrNoteService'
import { IHrNoteHistoryItem } from '../../../services/HrNote/types'

interface IProps {
  isOpen: boolean
  onClose: () => void
  jobApplicationId: number
}

export function HrNoteHistoryModal({ isOpen, onClose, jobApplicationId }: IProps) {
  const service = new HrNoteService()
  const toast = useAppToast()
  const [history, setHistory] = useState<IHrNoteHistoryItem[]>([])
  const [groupingWindowMinutes, setGroupingWindowMinutes] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!isOpen) return

    let isActive = true

    setHistory([])
    setGroupingWindowMinutes(null)
    setLoading(true)

    service
      .history(jobApplicationId)
      .then((response) => {
        if (!isActive) return
        setHistory(response.history)
        setGroupingWindowMinutes(response.grouping_window_minutes)
      })
      .catch(() => {
        if (!isActive) return
        toast.error('Erro ao carregar histórico da Nota RH')
      })
      .finally(() => {
        if (!isActive) return
        setLoading(false)
      })

    return () => {
      isActive = false
    }
  }, [isOpen, jobApplicationId])

  return (
    <FormModal isOpen={isOpen} onClose={onClose} title="Histórico da Nota RH">
      {groupingWindowMinutes !== null && (
        <Alert status="info" variant="left-accent" mb="4" fontSize="sm" borderRadius="md">
          <AlertIcon />
          <AlertDescription>
            Edições consecutivas do mesmo usuário feitas em um intervalo de até{' '}
            <strong>{groupingWindowMinutes} minutos</strong> são consolidadas em um único registro para evitar poluir o
            histórico. Apenas o último valor salvo dentro dessa janela é mantido.
          </AlertDescription>
        </Alert>
      )}
      {loading ? (
        <LoadingBlock />
      ) : history.length === 0 ? (
        <Text color="gray.500" textAlign="center" py="10">
          Nenhum histórico disponível.
        </Text>
      ) : (
        <VStack spacing="4" align="stretch">
          {history.map((item) => (
            <Box key={item.id} borderWidth="1px" borderRadius="md" p="4" bg="gray.50">
              <Box display="flex" alignItems="center" mb="2">
                <Avatar size="sm" name={item.author.name} src={item.author.picture ?? undefined} mr="3" />
                <Box>
                  <Text fontWeight="semibold" fontSize="sm">
                    {item.author.name}
                  </Text>
                  <Text variant="caption">{formatDateTime(item.updated_at)}</Text>
                </Box>
              </Box>
              <Text whiteSpace="pre-wrap" fontSize="sm">
                {item.note || (
                  <Text as="span" color="gray.400" fontStyle="italic">
                    (vazio)
                  </Text>
                )}
              </Text>
            </Box>
          ))}
        </VStack>
      )}
    </FormModal>
  )
}
